import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { PureComponent, type ReactElement } from "react";
import { connect } from "react-redux";
import {
  clearSubmitErrors,
  formValueSelector,
  type InjectedFormProps,
  reduxForm,
  SubmissionError,
  type Validator,
} from "redux-form";
import { HStack, InfoToggletip } from "widgets";

import {
  CELL_TYPE,
  COS_ANALYTICAL_ID,
  COS_ANNOTATION_ID,
  DEFAULT_SAMPLE_STATUS_SET_ID,
  REPRESENTER_COLUMN,
} from "@/settings/settings.js";
import { SelForm } from "@/ui-modules/core/form/layout/Form.js";

import { getFormError, getFormWarning } from "../../app-utils/form.js";
import {
  checkAttributeDefaults,
  findAutomaticParentAttributeWithMissingDefault,
  isConfigurableParentAttribute,
} from "../../app-utils/sample.js";
import { trimOrThrow } from "../../app-utils/string.js";
import {
  type Attribute,
  type InputAttributeDefaultInput,
  type InputParentTypeInput,
  type InputParentTypeSetInput,
  type InputSampleTypeInput,
  type InputTypeAttributeInput,
  type ParentType,
  type ParentTypeSet,
  type RawAttributeDefault,
  type SampleType,
  type StatusSet,
} from "../../graphql.js";
import {
  buildDefaultsMapByAttributeId,
  isDefinedNumber,
  loadAttributes,
  loadAttributesForDecoration,
  loadParentTypesAvailableForSampleTypeCreation,
  loadSampleStatusSets,
  modifySampleType,
  saveNewSampleType,
  validateSampleTypeExists,
} from "../../redux/modules/sample-admin.js";
import { type IAppState } from "../../redux/reducer.js";
import CheckboxField, { CheckboxReduxFormField } from "../core/redux-form/CheckboxField.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import {
  default as ParentTypeField,
  HIDDEN_TOUCH,
  IS_PRIMARY_FIELD,
  IS_RECYCLABLE_FIELD,
  ParentTypeReduxFormField,
} from "../core/redux-form/ParentTypeField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.js";
import { FieldTitle } from "../playbooks/FieldTitle.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../playbooks/PlaybookWizardStep.js";
import { type IAttributeWithDefault } from "./AddSampleTypeDialog.js";
import AttachAttributesManager from "./AttachAttributesManager.js";
import { default as ConfigureParentTypeAttributeDialog } from "./ConfigureParentTypeAttributeDialog.js";

export interface IAddSampleTypeScreenOwnProps extends IPlaybookWizardStepProvidedProps {
  sampleType?: SampleType; // editing case
  attributeDefaults?: Partial<RawAttributeDefault>[]; // editing case
}

interface IAddSampleTypeScreenAppStateProps {
  sampleTypes: SampleType[];
  parentTypeValues: ParentTypeSet[];
  statusSets: StatusSet[];
  globalFormError: string;
  globalFormWarning: string;
  initialValues: Partial<FormData>; // from InjectedFormProps
}

interface IAddSampleTypeScreenDispatchProps {
  saveNewSampleType: (config: InputSampleTypeInput) => Promise<SampleType>;
  modifySampleType: (data: InputSampleTypeInput) => Promise<SampleType>;
  loadParentTypes: () => Promise<ParentType[]>;
  loadSampleStatusSets: () => Promise<StatusSet[]>;
  loadAttributes: () => Promise<Attribute[]>;
  validateTypeExists: (name: string) => Promise<boolean>;
  clearFormError: () => void;
  loadAttributeForDecoration: () => Promise<Attribute[]>;
}

export interface IAddSampleTypeScreenProps
  extends IAddSampleTypeScreenAppStateProps,
    IAddSampleTypeScreenDispatchProps,
    IAddSampleTypeScreenOwnProps,
    InjectedFormProps<FormData, any>,
    IPlaybookWizardStepInjectedProps {}

function mapStateToProps(state: IAppState, ownProps: IAddSampleTypeScreenOwnProps): IAddSampleTypeScreenAppStateProps {
  const initValues = ownProps.sampleType
    ? {
        [AddSampleTypeScreenImpl.FIELD_NAME]: ownProps.sampleType.name,
        [AddSampleTypeScreenImpl.FIELD_DESCRIPTION]: ownProps.sampleType.description,
        [AddSampleTypeScreenImpl.FIELD_ALLOW_MANUAL_STATUS_CHANGE]: ownProps.sampleType.allowManualStatusChange,
        [AddSampleTypeScreenImpl.FIELD_PARENT_LIST]: ownProps.sampleType.parentTypeSets,
        [AddSampleTypeScreenImpl.FIELD_STATUS_SET]: ownProps.sampleType.statusSet,
      }
    : {};
  return {
    sampleTypes: state.sampleAdmin.sampleTypes,
    statusSets: state.sampleAdmin.sampleStatuses,
    parentTypeValues: formValueSelector(AddSampleTypeScreenImpl.FORM_NAME)(
      state,
      AddSampleTypeScreenImpl.FIELD_PARENT_LIST,
    ),
    globalFormError: getFormError(AddSampleTypeScreenImpl.FORM_NAME, state),
    globalFormWarning: getFormWarning(AddSampleTypeScreenImpl.FORM_NAME, state),
    initialValues: initValues,
  };
}

function mapDispatchToProps(dispatch): IAddSampleTypeScreenDispatchProps {
  return {
    saveNewSampleType: (config: InputSampleTypeInput) => dispatch(saveNewSampleType(config)),
    modifySampleType: (data: InputSampleTypeInput) => dispatch(modifySampleType(data)),
    loadParentTypes: () => dispatch(loadParentTypesAvailableForSampleTypeCreation()),
    loadSampleStatusSets: () => dispatch(loadSampleStatusSets()),
    loadAttributes: () => dispatch(loadAttributes()),
    validateTypeExists: (name: string) => dispatch(validateSampleTypeExists(name)),
    clearFormError: () => dispatch(clearSubmitErrors(AddSampleTypeScreenImpl.FORM_NAME)),
    loadAttributeForDecoration: () => dispatch(loadAttributesForDecoration()),
  };
}

interface IAddSampleTypeDialogState {
  assignedAttributes?: IAttributeWithDefault[];
  availableAttributes?: IAttributeWithDefault[]; // all attributes, but filtered by COS
  parentTypeAttributes?: Record<number, IAttributeWithDefault[]>; // map by parentId
  isLoadingAvailableAttributes?: boolean;
  openedParentTypeConfigureId?: number;
  availableParentTypes?: ParentType[]; // together with the parent attributes and their vocabulary terms so that each attribute can be configured
  isLoadingParenTypes?: boolean;
  isLoadingStatusSets?: boolean;
  hasSamplesAttached?: boolean;
  attributesValidationError?: string;
  attributesForDecoration: Attribute[];
}

class AddSampleTypeScreenImpl extends PureComponent<IAddSampleTypeScreenProps, IAddSampleTypeDialogState> {
  public static FORM_NAME = "ADD_SAMPLE_TYPE_SCREEN";

  public static FIELD_NAME = "NAME";
  public static FIELD_DESCRIPTION = "DESCRIPTION";
  public static FIELD_PARENT_LIST = "PARENT_LIST";
  public static FIELD_STATUS_SET = "STATUS_SET";
  public static FIELD_ALLOW_MANUAL_STATUS_CHANGE = "FIELD_ALLOW_MANUAL_STATUS_CHANGE";

  // for decoding the column identifiers have a look at AttributeTableColumnAdapterMap
  public static columnDefs: any[] = [
    { name: "Configure", cellType: CELL_TYPE.BOOLEAN, field: "editAction", fixed: true },
    { name: "Name", columnId: 1401, cellType: CELL_TYPE.STRING, field: "attributeName", fixed: true },
    { name: "Group Name", columnId: 1402, cellType: CELL_TYPE.STRING, field: "groupName" },
    { name: "Default Value", cellType: CELL_TYPE.STRING, field: "attributeDefault" },

    { name: "Attribute Type", columnId: 1404, cellType: CELL_TYPE.STRING, field: "attributeType.name" },
    { name: "Column Set", columnId: 1403, cellType: CELL_TYPE.STRING, field: "columnSet.name" },
    { name: "Sample Types", columnId: 1405, cellType: CELL_TYPE.STRING_LIST, field: "sampleTypes.name" },
    // {"name":"Parent Type","columnId":1406,"cellType":CELL_TYPE.STRING,"field":"parentType.name"},
    {
      name: "Vocabulary Terms",
      columnId: 1407,
      cellType: CELL_TYPE.STRING_LIST,
      field: "vocabularyTerms.name",
    },

    { name: "Modifiable", columnId: 1422, cellType: CELL_TYPE.BOOLEAN, field: "modifiable" },
    { name: "Mandatory", columnId: 1420, cellType: CELL_TYPE.BOOLEAN, field: "mandatory" },
    { name: "Automatic", columnId: 1421, cellType: CELL_TYPE.BOOLEAN, field: "automatic" },
    { name: "Disabled", columnId: 1419, cellType: CELL_TYPE.BOOLEAN, field: "disabled" },
    { name: "Edit Once", columnId: 1423, cellType: CELL_TYPE.BOOLEAN, field: "editOnce" },
    { name: "Frozen", columnId: 1424, cellType: CELL_TYPE.BOOLEAN, field: "frozen" },
    { name: "Accept Multiple Values", columnId: 1426, cellType: CELL_TYPE.BOOLEAN, field: "multipleValues" },
    { name: "Is Multiple Values Head", columnId: 1427, cellType: CELL_TYPE.BOOLEAN, field: "isHead" },

    { name: "Max Length", columnId: 1411, cellType: CELL_TYPE.NUMBER, field: "maxLength" },
    { name: "Max Value", columnId: 1410, cellType: CELL_TYPE.NUMBER, field: "maxValue" },
    { name: "Min Value", columnId: 1409, cellType: CELL_TYPE.NUMBER, field: "minValue" },
    { name: "Decimals", columnId: 1412, cellType: CELL_TYPE.NUMBER, field: "decimals" },

    { name: "Formula", columnId: 1415, cellType: CELL_TYPE.STRING, field: "formulaText" },
    { name: "Pattern", columnId: 1413, cellType: CELL_TYPE.STRING, field: "pattern" },
    { name: "Pattern Hint", columnId: 1414, cellType: CELL_TYPE.STRING, field: "patternHint" },
    { name: "Represented Column", columnId: 1418, cellType: CELL_TYPE.STRING, field: "representedColumn" },
    { name: "SQL Statement", columnId: 1416, cellType: CELL_TYPE.STRING, field: "sqlStatement" },
    { name: "Coloring Rule", columnId: 1429, cellType: CELL_TYPE.STRING, field: "coloringRule" },
    { name: "Id Accessor", columnId: 1417, cellType: CELL_TYPE.STRING, field: "idAccessor" },
    { name: "Id", cellType: CELL_TYPE.NUMBER, field: "id" },
  ];

  constructor(props: IAddSampleTypeScreenProps) {
    super(props);

    // initialization of the parent type attributes with custom defaults/template for each of them
    const parentTypeAttributes: Record<number, IAttributeWithDefault[]> = {}; // map by parentId
    if (props.sampleType) {
      // sample type editing case
      props.sampleType.parentTypeSets.forEach((parentTypeSet: ParentTypeSet) => {
        parentTypeAttributes[parentTypeSet.parentType.id] = this.computeTypeAttributesWithDefaults(
          parentTypeSet.parentType.parentAttributes,
          this.props.attributeDefaults,
        );
      });
      // new sample type case not required because initially no parent type is selected
    }

    this.state = {
      assignedAttributes: this.computeTypeAttributesWithDefaults(
        props.sampleType?.sampleAttributes,
        this.props.attributeDefaults,
      ),
      parentTypeAttributes: parentTypeAttributes,
      availableAttributes: [],
      isLoadingAvailableAttributes: false,
      openedParentTypeConfigureId: null,
      availableParentTypes: [],
      isLoadingParenTypes: false,
      isLoadingStatusSets: false,
      attributesValidationError: null,
      attributesForDecoration: [],
      hasSamplesAttached: props.sampleType?.sampleCount > 0,
    };
  }

  suggestDefaultTemplateForParentTypeNameAttribute = (attributes: IAttributeWithDefault[] | null | undefined) => {
    const parentNameAttribute: IAttributeWithDefault = attributes?.find(
      (attribute: IAttributeWithDefault) => attribute.representedColumn === REPRESENTER_COLUMN.NAME,
    );
    if (parentNameAttribute) {
      const suggestedParentName = `$PARENT_TYPE() $DATE(format=yyyy-MMM-dd hh:mm:ss)`;
      parentNameAttribute.attributeDefault = {
        template: suggestedParentName,
      };
    }
  };

  // update the map of Parent Type Attributes with their default/template values when a parent type is added or removed
  parentTypeAdded = (parentType: ParentType) => {
    const parentTypeAttributes = { ...this.state.parentTypeAttributes };
    parentTypeAttributes[parentType.id] = this.computeTypeAttributesWithDefaults(
      parentType.parentAttributes,
      // In the editing case, use the defaults from the sample type (via this.props), in new sample types use the defaults that come with the parent type (reflecting the synced value)
      // Note that this is more conservative than required because both should always have the same value (because parent attribute defaults/templates are synchronized)
      this.props.sampleType
        ? this.props.attributeDefaults
        : buildDefaultsMapByAttributeId(parentType.attributeDefaults),
    );
    // suggest a template for the parent name only if this is a new parent type that is not used in any sample type yet
    if (!(parentType.sampleTypes?.length > 0)) {
      this.suggestDefaultTemplateForParentTypeNameAttribute(parentTypeAttributes[parentType.id]);
    }
    this.setState({ parentTypeAttributes: parentTypeAttributes });
  };

  parentTypeRemoved = (parentType: ParentType) => {
    const parentTypeAttributes = { ...this.state.parentTypeAttributes };
    delete parentTypeAttributes[parentType.id];
    this.setState({ parentTypeAttributes: parentTypeAttributes });
  };

  computeTypeAttributesWithDefaults = (
    attributes: Attribute[],
    attributeDefaults: Partial<RawAttributeDefault>[],
  ): IAttributeWithDefault[] => {
    if (attributes) {
      return attributes.map((attribute: Attribute) => {
        return {
          ...attribute,
          attributeDefault: attributeDefaults?.[attribute.id],
        };
      });
    }
    return [];
  };

  initAvailableAttributes = (attributes: IAttributeWithDefault[]) => {
    const availableAttributes = attributes.filter(
      (attribute: IAttributeWithDefault) =>
        attribute.columnSet.id === COS_ANNOTATION_ID || attribute.columnSet.id === COS_ANALYTICAL_ID,
    );
    this.setState({ availableAttributes: availableAttributes });
    return availableAttributes;
  };

  initPreselectedAttributes = (availableAttributes: IAttributeWithDefault[]) => {
    // only creation case, not editing case
    if (!this.props.sampleType) {
      // get latest list of attributes and initialize the list of assigned attributes
      //  to all representing attributes of a Sample
      const preselectedAttributes: IAttributeWithDefault[] = availableAttributes
        .filter((a) => !!a.representedColumn)
        .sort((a, b) => a.id - b.id);
      // Select the initial represented attributes (name, status, created, creator, ...)
      //  set them all to 'cannot delete'
      this.setAssignedAttributes(preselectedAttributes);
    }
  };

  loadAttributes = () => {
    return this.props
      .loadAttributes()
      .catch((error) => {
        throw new SubmissionError({ _error: "Could not load the list of attributes: " + error.message });
      })
      .then(this.initAvailableAttributes);
  };

  loadSampleStatusSets = () => {
    this.setState({ isLoadingStatusSets: true });
    return this.props
      .loadSampleStatusSets()
      .then((statusSet) => {
        if (!this.props.sampleType) {
          this.props.initialize({
            [AddSampleTypeScreenImpl.FIELD_STATUS_SET]: statusSet.find(
              (statusSet) => statusSet.id === DEFAULT_SAMPLE_STATUS_SET_ID,
            ),
          });
        }
      })
      .then(() => this.setState({ isLoadingStatusSets: false }))
      .catch((error) => {
        throw new SubmissionError({ _error: "Could not load the list of status sets: " + error.message });
      });
  };

  // we do not want the regular listing of parent types, we also need their attributes together with all the props needed for proper configuration of attributes
  loadParentTypes = () => {
    this.setState({ isLoadingParenTypes: true });
    return this.props
      .loadParentTypes()
      .then((parentTypes: ParentType[]) => {
        // sort alphabetically, english, case-insensitive
        parentTypes.sort((attr1, attr2) => attr1.name.localeCompare(attr2.name, "en", { sensitivity: "base" }));
        this.setState({
          availableParentTypes: parentTypes,
          isLoadingParenTypes: false,
        });
      })
      .catch((error) => {
        throw new SubmissionError({ _error: "Could not load the list of available parent types: " + error.message });
      });
  };

  componentDidMount() {
    this.loadParentTypes();
    this.setState({ isLoadingAvailableAttributes: true });
    this.loadAttributes()
      .then(this.initPreselectedAttributes)
      .then(() => this.setState({ isLoadingAvailableAttributes: false }));

    // make sure to show potential default validation error for sample attributes
    this.validateSampleAttributeDefaults();

    this.props.loadAttributeForDecoration().then((attributes) => {
      this.setState({ attributesForDecoration: attributes });
    });

    this.loadSampleStatusSets();
  }

  setAssignedAttributes = (selectedAttributes: IAttributeWithDefault[]) => {
    this.setState(
      {
        assignedAttributes: selectedAttributes,
      },
      this.validateSampleAttributeDefaults,
    );
    // hide previous validation errors because they may be resolved now
    this.props.clearFormError();
  };

  validateSampleAttributeDefaults = () => {
    this.setState({
      attributesValidationError: checkAttributeDefaults(this.state.assignedAttributes),
    });
  };

  isEmptyDefault = (d: InputAttributeDefaultInput): boolean => {
    return !d || (!isDefinedNumber(d.defaultNumber) && !d.defaultText && !d.defaultVocabularyTermId && !d.template);
  };

  buildTypeAttributeInput = (a: IAttributeWithDefault, index: number): InputTypeAttributeInput => {
    let defaultInput: InputAttributeDefaultInput = a.attributeDefault && {
      defaultNumber: a.attributeDefault.defaultNumber,
      defaultText: a.attributeDefault.defaultText,
      defaultVocabularyTermId: a.attributeDefault.defaultTerm?.id,
      template: a.attributeDefault.template,
    };

    if (this.isEmptyDefault(defaultInput)) {
      defaultInput = undefined;
    }

    return {
      attributeId: a.id,
      sortOrder: index + 1,
      attributeDefault: defaultInput,
    };
  };

  handleSubmitData = (values): Promise<SampleType | ParentType> => {
    const parents = values[AddSampleTypeScreenImpl.FIELD_PARENT_LIST] || [];
    if (parents.length === 0) {
      throw new SubmissionError({
        [AddSampleTypeScreenImpl.FIELD_PARENT_LIST]: "At least one parent type has to be assigned.",
      });
    }

    const statusSet = values[AddSampleTypeScreenImpl.FIELD_STATUS_SET];
    if (!statusSet) {
      throw new SubmissionError({
        [AddSampleTypeScreenImpl.FIELD_STATUS_SET]: "Please select a status set.",
      });
    }

    // Setable SampleType properties:
    // * defined here
    //    name, description, allowManualStatusChange
    //    parentTypeSetInput from parents, primaryParentType,
    //    attributes
    // * set on the middle tier
    //    creator,created, id
    // * all derived state variables like
    //    parentAttributes, sampleCount, ...
    const savedData: InputSampleTypeInput = {
      statusSetID: values[AddSampleTypeScreenImpl.FIELD_STATUS_SET].id,
      id: this.props.sampleType && this.props.sampleType.id,
      name: trimOrThrow(
        values[AddSampleTypeScreenImpl.FIELD_NAME],
        new SubmissionError({ _error: "Name value cannot be empty and must contain non white space characters" }),
      ),
      description: trimOrThrow(values[AddSampleTypeScreenImpl.FIELD_DESCRIPTION], null),
      allowManualStatusChange: !!values[AddSampleTypeScreenImpl.FIELD_ALLOW_MANUAL_STATUS_CHANGE],
      parentTypeSetInput: values[AddSampleTypeScreenImpl.FIELD_PARENT_LIST].map(
        (p: ParentTypeSet): InputParentTypeSetInput => {
          const parentTypeAttributes: InputParentTypeInput = {
            id: p.parentType.id,
            attributes: this.state.parentTypeAttributes[p.parentType.id].map(this.buildTypeAttributeInput),
          };

          return {
            id: p.id,
            parentType: parentTypeAttributes,
            automatic: !p.isPrimary,
            mandatory: true, // at the moment we only support mandatory parent types to be added via UI (also automatic ones need to be mandatory for formal reasons)
            recyclable: !!p.isRecyclable,
          };
        },
      ),
      // ParentTypeInputData
      attributes: this.state.assignedAttributes.map(this.buildTypeAttributeInput),
    };

    const submitData = this.props.sampleType ? this.props.modifySampleType : this.props.saveNewSampleType;

    return submitData(savedData).then(
      (payload: SampleType) => {
        this.props.closeWizard();
        return payload;
      },
      (rejected) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  handleParentPrimarySettingChanged = (index: number) => {
    // if primary will be switched off, switch off the recyclable flag
    const parentTypeSets: ParentTypeSet[] = this.props.parentTypeValues;

    // note that onChange is called BEFORE the change applies, therefore we must do the change if the primary field is set to true
    if (parentTypeSets[index][IS_PRIMARY_FIELD] && parentTypeSets[index][IS_RECYCLABLE_FIELD]) {
      this.props.change(`${AddSampleTypeScreenImpl.FIELD_PARENT_LIST}[${index}].${IS_RECYCLABLE_FIELD}`, false);
    }

    // switch off other potential primary settings
    if (!parentTypeSets[index][IS_PRIMARY_FIELD]) {
      parentTypeSets.forEach((_p: ParentTypeSet, i: number) => {
        if (i !== index) {
          this.props.change(`${AddSampleTypeScreenImpl.FIELD_PARENT_LIST}[${i}].${IS_PRIMARY_FIELD}`, false);
          this.props.change(`${AddSampleTypeScreenImpl.FIELD_PARENT_LIST}[${i}].${IS_RECYCLABLE_FIELD}`, false);
        }
      });
    }
  };

  handleParentRecyclableSettingChanged = (_index: number) => {
    // nothing to do here right now...
  };

  openParentAttributeConfigureDialog = (parentTypeId: number) => {
    this.setState({ openedParentTypeConfigureId: parentTypeId });
  };

  // rules to remove a sample attribute
  getRemoveAttributesDisabledTooltip = (selectedAttributeIds: number[]) => {
    // editing case
    if (this.props.sampleType && this.props.sampleType.sampleCount > 0) {
      return "A sample type attribute can only be removed if there are no samples associated with the sample type";
    } else {
      const selectedAttributes: Attribute[] = selectedAttributeIds.map((selectedId: number) =>
        this.state.assignedAttributes.find((assignedAttribute: Attribute) => assignedAttribute.id === selectedId),
      );

      if (selectedAttributes.find((attribute: Attribute) => !!attribute.representedColumn)) {
        return "A sample type attribute cannot be removed if it is a representer attribute";
      } else if (
        selectedAttributes.find((attribute: Attribute) => attribute.referencedBy && attribute.referencedBy.length > 0)
      ) {
        return "A sample type attribute cannot be removed if it is dependent on the formula of another existing attribute";
      }
    }
  };

  render(): ReactElement {
    const parentTypeToConfigure: ParentType =
      this.state.availableParentTypes &&
      this.state.openedParentTypeConfigureId &&
      this.state.availableParentTypes.find(
        (parentType: ParentType) => parentType.id === this.state.openedParentTypeConfigureId,
      );

    return (
      <PlaybookWizardStep
        {...this.props}
        playbookPage={this.props.playbookPage}
        wizardFooterData={{
          error: this.props.globalFormError || this.props.error || this.state.attributesValidationError,
          warning: this.props.globalFormWarning,
          nextButtonProps: {
            label: this.props.sampleType ? "Apply" : "Save",
            isLoading: this.props.submitting || this.state.isLoadingAvailableAttributes,
            onPress: this.props.handleSubmit(this.handleSubmitData),
          },
        }}
      >
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "20px" }}>
            <div id="right-dialog-grid-col" style={{ width: "60%" }}>
              <SelForm>
                <InputReduxFormField
                  componentClass="input"
                  name={AddSampleTypeScreenImpl.FIELD_NAME}
                  label={<FieldTitle label={"Name"} mandatory />}
                  placeholder={`Sample type name`}
                  maxLength={DB_FIELD_MAX_LENGTH.ATTRIBUTE.NAME}
                  component={InputField}
                  validate={AddSampleTypeScreenImpl.localNameValidator}
                />

                <CheckboxReduxFormField
                  name={AddSampleTypeScreenImpl.FIELD_ALLOW_MANUAL_STATUS_CHANGE}
                  label="Allow manual status change"
                  component={CheckboxField}
                />

                <ParentTypeReduxFormField
                  name={AddSampleTypeScreenImpl.FIELD_PARENT_LIST}
                  title={
                    <FieldTitle
                      label={
                        <HStack>
                          Parent types
                          <InfoToggletip
                            content={
                              <>
                                Parent types are used to manage data that are shared across mutliple related samples.
                                Examples include submission or experiment information.
                              </>
                            }
                          />
                        </HStack>
                      }
                      mandatory
                    />
                  }
                  options={this.state.availableParentTypes}
                  component={ParentTypeField}
                  isLoading={this.state.isLoadingParenTypes}
                  validate={this.localParentSelectionValidator}
                  handlePrimaryChanged={this.handleParentPrimarySettingChanged}
                  handleRecyclableChanged={this.handleParentRecyclableSettingChanged}
                  openConfigureDialog={this.openParentAttributeConfigureDialog}
                  onOpenList={this.props.loadParentTypes}
                  sampleTypeHasData={this.props.sampleType && this.props.sampleType.sampleCount > 0}
                  parentTypeAdded={this.parentTypeAdded}
                  parentTypeRemoved={this.parentTypeRemoved}
                />
                <ReactSelectReduxFormField
                  name={AddSampleTypeScreenImpl.FIELD_STATUS_SET}
                  label={<FieldTitle label={"Sample status set"} mandatory />}
                  component={ReactSelectField}
                  placeholder={`Select status set...`}
                  options={this.props.statusSets}
                  isSearchable={true}
                  isClearable={false}
                  isLoading={this.state.isLoadingStatusSets}
                  getOptionLabel={(option: StatusSet) => option.name}
                  getOptionValue={(option: StatusSet) => option.id}
                  validate={this.localStatusSetSelectionValidator}
                  isDisabled={this.state.hasSamplesAttached}
                  tooltip={
                    this.state.hasSamplesAttached &&
                    "Samples are already associated with this sample type. Changing the status set might lead to inconsistencies."
                  }
                />
              </SelForm>
            </div>
            <div id="left-dialog-grid-col" style={{ width: "40%" }}>
              <SelForm>
                <InputReduxFormField
                  componentClass="textarea"
                  name={AddSampleTypeScreenImpl.FIELD_DESCRIPTION}
                  label="Description"
                  placeholder={`Add a description`}
                  maxLength={DB_FIELD_MAX_LENGTH.ATTRIBUTE.DESCRIPTION}
                  component={InputField}
                  rows={8}
                />
              </SelForm>
            </div>
          </div>

          <AttachAttributesManager
            columnDefs={AddSampleTypeScreenImpl.columnDefs}
            assignedAttributes={this.state.assignedAttributes}
            availableAttributes={this.state.availableAttributes}
            handleAttributesChanged={this.setAssignedAttributes}
            refreshAvailableAttributes={this.loadAttributes}
            getRemoveAttributesDisabledTooltip={this.getRemoveAttributesDisabledTooltip}
            isLoadingAvailableAttributes={this.state.isLoadingAvailableAttributes}
            attributesForDecoration={this.state.attributesForDecoration}
          />
        </div>
        {this.state.openedParentTypeConfigureId && this.state.parentTypeAttributes && (
          <ConfigureParentTypeAttributeDialog
            onApply={this.handleApplyConfigureParentTypeAttribute}
            onCancel={this.closeParentTypeAttributesConfigurationModal}
            templateAttributes={this.state.parentTypeAttributes[this.state.openedParentTypeConfigureId]}
            attributes={this.state.parentTypeAttributes[this.state.openedParentTypeConfigureId]}
            parentTypeName={parentTypeToConfigure?.name}
            usedInOtherSampleTypes={
              !!parentTypeToConfigure?.sampleTypes?.filter((s) => s.id !== this.props.sampleType?.id).length
            }
          />
        )}
      </PlaybookWizardStep>
    );
  }

  closeParentTypeAttributesConfigurationModal = () => {
    this.setState({ openedParentTypeConfigureId: null });
  };

  handleApplyConfigureParentTypeAttribute = (configuredAttributes: IAttributeWithDefault[]) => {
    const parentTypeAttributes = { ...this.state.parentTypeAttributes };
    // when applying default/template value to the parent type attribute we store it back to the local state (so that it can be part of the submission payload later)
    parentTypeAttributes[this.state.openedParentTypeConfigureId] = configuredAttributes;

    this.props.clearFormError(); // in case of submit issue due to parent attribute defaults, we reset the async form error when making any change

    const configuredParentTypeId: number = this.state.openedParentTypeConfigureId;
    this.setState(
      {
        parentTypeAttributes: parentTypeAttributes,
        openedParentTypeConfigureId: null,
      },
      () => {
        // make sure the FIELD_PARENT_LIST is marked as touched/modified and therefore trigger the validation again [SEL6-1706, SEL6-1547]
        const fieldIndex: number = this.props.parentTypeValues.findIndex(
          (p: ParentTypeSet) => p.parentType.id === configuredParentTypeId,
        );
        this.props.change(`${AddSampleTypeScreenImpl.FIELD_PARENT_LIST}[${fieldIndex}].${HIDDEN_TOUCH}`, Math.random());
      },
    );
  };

  localStatusSetSelectionValidator: Validator = (value: any, _allValues?: any, _props?: any): string | undefined => {
    if (!value) {
      return `Please select a status set.`;
    }
  };

  localParentSelectionValidator: Validator = (value: any, _allValues?: any, _props?: any): string | undefined => {
    if (!value || value.length === 0) {
      return `At least one parent type has to be assigned`;
    }

    // check if there is a primary parent type selected
    const primaryParentTypeSet: ParentTypeSet = value.find((p: ParentTypeSet) => !!p.isPrimary);
    if (!primaryParentTypeSet) {
      return `There must be exactly one primary parent type`;
    }

    const primaryParentTypeAttributes: IAttributeWithDefault[] =
      this.state.parentTypeAttributes[primaryParentTypeSet.parentType.id];
    const missingDefaultAttributeForPrimaryParentType: IAttributeWithDefault =
      primaryParentTypeAttributes && findAutomaticParentAttributeWithMissingDefault(primaryParentTypeAttributes);
    if (missingDefaultAttributeForPrimaryParentType) {
      return `All automatic attributes of the primary parent type must have a default value (attribute ${missingDefaultAttributeForPrimaryParentType.attributeName}).`;
    }

    const configurableNonPrimaryParentTypeSet: ParentTypeSet[] = value.filter(
      (p: ParentTypeSet) => !p.isPrimary && p.parentType,
    );
    if (
      configurableNonPrimaryParentTypeSet &&
      !!configurableNonPrimaryParentTypeSet.find((parentTypeSet: ParentTypeSet) => {
        const nonPrimaryParentTypeAttributes: IAttributeWithDefault[] =
          this.state.parentTypeAttributes[parentTypeSet.parentType.id];
        return (
          nonPrimaryParentTypeAttributes &&
          !!nonPrimaryParentTypeAttributes
            .filter(isConfigurableParentAttribute)
            .find((attribute: IAttributeWithDefault) => !attribute.attributeDefault)
        );
      })
    ) {
      return `All configurable attributes of the non-primary parent types must have a default value. Click on the configuration button to set up default values.`;
    }

    const selectedPrimaryParentTypeId = primaryParentTypeSet?.parentType?.id;
    // The following can only be one single parent attribute, because there is just one name representer in a parent type.
    // It must have a template since a constant default could not be used as parent name.
    const notModifiableNameRepresenterAttributeWithoutTemplate =
      selectedPrimaryParentTypeId && this.state.parentTypeAttributes
        ? this.state.parentTypeAttributes[selectedPrimaryParentTypeId]?.find(
            (p) => p.representedColumn === REPRESENTER_COLUMN.NAME && !p.modifiable && !p.attributeDefault,
          )?.attributeName
        : undefined;
    if (notModifiableNameRepresenterAttributeWithoutTemplate) {
      return `The following unmodifiable parent name attribute is lacking a template: ${notModifiableNameRepresenterAttributeWithoutTemplate}. Click on the configuration button to set up a default value.`;
    }

    // check if parent attribute defaults are matching restrictions like maxLength or min/maxValue
    if (value && value.length > 0) {
      let message: string = null;
      // last message wins, but expected number of messages is very small
      value.forEach((ps: ParentTypeSet) => {
        const psMessage = checkAttributeDefaults(this.state.parentTypeAttributes[ps.parentType.id], ps.parentType);
        if (psMessage) {
          message = psMessage;
        }
      });
      return message;
    }
  };

  static localNameValidator: Validator = (
    value: any,
    _allValues?: any,
    props?: IAddSampleTypeScreenProps,
  ): string | undefined => {
    const name: string = !value ? "" : value.trim();

    // editing case
    if (props.sampleType && name === props.sampleType.name) {
      return;
    }
    if (name !== "") {
      // TBD: check for collision with initial name
      //  if props.<sampleType> &&  name === props.<sampleType>.name --> error
      // validate locally versus already loaded sample types
      if (props.sampleTypes.find((say) => say.name === name)) {
        return "This sample type name is already in use";
      }
    } else {
      return "The sample type name is required";
    }
    // if any of the local checks failed short-cut and report that first.
    return undefined;
  };

  static async asyncValidate(values, _dispatch, props: IAddSampleTypeScreenProps) {
    const CLASS = AddSampleTypeScreenImpl;

    if (!values) {
      return;
    }

    //const parentListValues = ... (get from values)
    //if (!newOutfactoredValidate(parentListValues)) {
    //  return Promise.resolve({[CLASS.FIELD_PARENT_LIST]: 'test'});
    //}

    // cleanup name
    const name = values[CLASS.FIELD_NAME] || "";

    // editing case
    if (props.sampleType && props.sampleType.name === name) {
      return;
    }

    if (await props.validateTypeExists(name)) {
      throw {
        [CLASS.FIELD_NAME]: "Sample type name is already in use in the database",
      };
    }
  }
}

const AddSampleTypeScreen = connect<
  IAddSampleTypeScreenAppStateProps,
  IAddSampleTypeScreenDispatchProps,
  IAddSampleTypeScreenOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm<{}, any>({
    form: AddSampleTypeScreenImpl.FORM_NAME,
    asyncValidate: AddSampleTypeScreenImpl.asyncValidate,
    touchOnChange: true,
    asyncBlurFields: [AddSampleTypeScreenImpl.FIELD_NAME], // fields that trigger validation onBlur
  })(AddSampleTypeScreenImpl) as any,
);

export { AddSampleTypeScreen };
export default AddSampleTypeScreen;
